import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Key, Link2 } from 'lucide-react';
import { memo, type CSSProperties } from 'react';
import type { Column } from '@hyphen-db-studio/schema-core';
import { tintedTableColors } from '../../lib/tableStyle';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useThemeStore } from '../../store/useThemeStore';

export interface TableNodeData {
  name: string;
  columns: Column[];
  color?: string;
  groupColor?: string;
  isSelected: boolean;
  fkColumnIds: Set<string>;
  [key: string]: unknown;
}

function TableNode({ id, data }: NodeProps & { data: TableNodeData }) {
  const theme = useThemeStore((s) => s.theme);
  // Set for a few seconds when the explanation links to one of this table's fields.
  const focusedColumnId = useDiagramViewStore((s) => (s.focusedColumn?.tableId === id ? s.focusedColumn.columnId : null));
  // During the guided story: this table is part of the current step ('focus') or is faded back ('dim'), plus any fields it names.
  const tour = useDiagramViewStore((s) => (s.tourFocus ? (s.tourFocus.nodeIds.has(id) ? 'focus' : 'dim') : null));
  const tourColumnIds = useDiagramViewStore((s) => s.tourFocus?.columnIds);
  const isDark = theme === 'dark';
  // A table that belongs to a colored group inherits a wash of that same color, so it
  // visually reads as part of that domain — the row text itself stays a fixed neutral
  // color regardless, so legibility never depends on how light/dark the tint is.
  const tint = data.color ?? data.groupColor;

  const cardStyle: CSSProperties = {};
  const headerStyle: CSSProperties = {};
  if (tint) {
    const colors = tintedTableColors(tint, isDark);
    cardStyle.backgroundColor = colors.body;
    headerStyle.backgroundColor = colors.header;
    headerStyle.backgroundImage = 'none'; // the neutral gradient class would otherwise paint over the tint
    headerStyle.color = colors.headerText;
    headerStyle.borderColor = colors.border;
    if (!data.isSelected) cardStyle.borderColor = colors.border;
  }

  return (
    <div
      className={`w-[240px] overflow-hidden rounded-xl border bg-white shadow-card transition-[box-shadow,opacity] duration-500 hover:shadow-card-hover dark:bg-gray-800 ${
        tour === 'dim' ? 'opacity-[0.18]' : ''
      } ${
        tour === 'focus' ? 'ring-4 ring-amber-400/70 shadow-[0_0_34px_rgba(251,191,36,0.6)]' : ''
      } ${
        data.isSelected
          ? 'border-sky-500 ring-4 ring-sky-400/30 shadow-[0_8px_28px_rgba(14,165,233,0.35)] dark:border-sky-400 dark:ring-sky-400/30'
          : tint
            ? ''
            : 'border-gray-300 dark:border-gray-600'
      }`}
      style={cardStyle}
    >
      <div
        className="border-b border-gray-200 bg-gradient-to-b from-gray-50 to-gray-100/70 px-3.5 py-2.5 text-sm font-semibold tracking-tight text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
        style={headerStyle}
      >
        {data.name}
      </div>
      <div>
        {data.columns.map((col) => (
          <div
            key={col.id}
            data-column-id={col.id}
            className={`relative flex items-center gap-1.5 border-b border-gray-100 px-3 py-1.5 text-xs last:border-b-0 dark:border-gray-700 ${
              col.id === focusedColumnId || (tour === 'focus' && tourColumnIds?.has(col.id)) ? 'bg-amber-200/80 ring-2 ring-inset ring-amber-500 dark:bg-amber-400/25' : ''
            }`}
          >
            <Handle type="target" position={Position.Left} id={col.id} className="!h-2 !w-2 !border-0 !bg-sky-400" />
            {col.settings.pk ? (
              <Key className="h-3 w-3 shrink-0 text-amber-500" />
            ) : data.fkColumnIds.has(col.id) ? (
              <Link2 className="h-3 w-3 shrink-0 text-sky-500" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <span className="flex-1 truncate font-medium text-gray-800 dark:text-gray-100">{col.name}</span>
            <span className="shrink-0 text-gray-400 dark:text-gray-500">
              {col.type}
              {!col.settings.pk && !col.settings.notNull && (
                <span className="ml-0.5 text-sky-500" title="nullable">
                  ?
                </span>
              )}
            </span>
            <Handle type="source" position={Position.Right} id={col.id} className="!h-2 !w-2 !border-0 !bg-sky-400" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default memo(TableNode);
